import { useCallback, useState } from 'react';
import { AgenciesService } from '@services/requests/housien-services/AgenciesService';
import type {
  ChangePlanRequest,
  ChangePlanResponse,
} from '@services/requests/housien-services/AgenciesService.subscription';

export interface UseChangePlanResult {
  /** Resolves true when the change was accepted, false when it was refused. */
  changePlan: (req: ChangePlanRequest) => Promise<boolean>;
  isPending: boolean;
  result: ChangePlanResponse | null;
  isError: boolean;
  reset: () => void;
}

/**
 * Provides a changePlan() action for upgrading or downgrading an agency subscription.
 *
 * Calls POST /agencies/{agencyId}/subscription/change-plan and surfaces the
 * isPending and result states. Returns HTTP 200 for immediate changes and 202
 * for pending_upgrade scenarios.
 */
export function useChangePlan(agencyId: string): UseChangePlanResult {
  const [isPending, setIsPending] = useState(false);
  const [result, setResult] = useState<ChangePlanResponse | null>(null);
  const [isError, setIsError] = useState(false);

  const changePlan = useCallback(
    async (req: ChangePlanRequest) => {
      setIsPending(true);
      setIsError(false);
      try {
        const response = await AgenciesService.changePlan(agencyId, req);
        setResult(response);
        return true;
      } catch {
        setIsError(true);
        return false;
      } finally {
        setIsPending(false);
      }
    },
    [agencyId],
  );

  const reset = useCallback(() => {
    setResult(null);
    setIsError(false);
  }, []);

  return { changePlan, isPending, result, isError, reset };
}
